/* eslint-disable max-len */
/* eslint-disable react/forbid-prop-types */
import { RightOutlined } from '@ant-design/icons';
import {
  Card,
  Col, Form,
  Modal,
  Row,
  Spin, message,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { I18nextProvider, Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  FillCompanyHeader, FormCompanyData, TaxNumberField, SelectProspectType, VerificationInformation,
} from '..';
import {
  CarrierProspectService, CompanyService, CustomerProspectService,
  GoogleAnalyticsService,
  TokenService,
} from '../../services';
import { InviteHr } from '../Forms';
import { ConfirmButton } from './buttons';

import env from '../../config/env';
import translations from '../../config/translations';
import {
  FlowType, ProspectType, FormViewType, Field, FIELD_TO_VALID_MAP, TaxNumber,
} from '../../types';
import { ErrorCode } from '../../utils/apiError';
import localStorageFacade, { LocalStorageKeys } from '../../utils/localStorageFacade';

const CompanyDetailsCard = styled(Card)`
  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  box-sizing: border-box;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  width: 634px;
  height: 243px;
`;

const FillCompanyData = ({
  uuid, regionFormConfig, password, username, flowType, traceId,
}) => {
  const { t } = useTranslation();
  const [existCompany, setExistCompany] = useState(false);
  const { fields: { taxNumber: { type: taxNumberType, validatable: validatableTaxNumber, extractFieldValue } } } = regionFormConfig;
  const [view, setView] = useState(validatableTaxNumber ? undefined : FormViewType.FILL_FORM);
  const [form] = Form.useForm();
  const [isSubmitIsActive, setSubmitIsActive] = useState(false);
  const [isError, setError] = useState(false);
  const [spin, setSpin] = useState(false);
  const [errorCode, setErrorCode] = useState();
  const [disabledCountrySelect, setDisabledCountrySelect] = useState(true);

  const validateForm = (fields) => {
    form.validateFields(fields)
      .then(() => setSubmitIsActive(true))
      .catch(() => setSubmitIsActive(false));
  };

  const getFieldToValid = () => (FIELD_TO_VALID_MAP.get(flowType));

  const fillFormData = (data, taxCode) => {
    setView(FormViewType.COMPLETED_FORM);
    form.setFieldsValue({ companyAddress: data?.companyAddress });
    form.setFieldsValue({ companyName: data?.companyName });
    form.setFieldsValue({ taxCode });
    if (data.companyAddress && data.companyName) {
      validateForm(getFieldToValid());
    }
  };

  const findCompany = () => {
    const vatNumber = form.getFieldValue(Field.VAT_NUMBER);
    const taxCode = vatNumber.substring(0, 2);
    setSpin(true);
    CompanyService.findCompany(taxCode, vatNumber.substring(2, vatNumber.Length))
      .then((d) => {
        switch (d.status) {
          case 200:
            fillFormData(d.data, taxCode);
            break;
          default:
            break;
        }
      })
      .catch((error) => {
        if (error.response) {
          setExistCompany(error.response.data.errorCode === ErrorCode.COMPANY_ALREADY_EXISTS);
          setErrorCode(error.response.data.errorCode);
        } else {
          setErrorCode(ErrorCode.COMPANY_EXTERNAL_VERIFICATION_FAILED);
        }
        setView(FormViewType.FILL_FORM);
      })
      .finally(() => {
        setSpin(false);
      });
  };

  const onFinish = (values) => {
    setSpin(true);
    const data = {
      taxNo: extractFieldValue(values.vatNumber),
      taxCode: values.vatCountryCode,
      companyName: values.companyName,
      companyAddress: values.companyAddress,
      password,
      prospectType: values.prospectType,
      prospectId: uuid,
    };

    const catchError = (e) => {
      if (e.response) {
        const isCompanyAlreadyExist = e.response.data.errorCode === ErrorCode.COMPANY_ALREADY_EXISTS;
        setExistCompany(isCompanyAlreadyExist);
        setError(!isCompanyAlreadyExist);
      }
      setSpin(false);
      // eslint-disable-next-line
      console.error(e);
      message.error(t('GENERIC_ERROR'));
    };

    const updateCompanyDataProspect = () => {
      CarrierProspectService.updateCompanyDataProspect(uuid, data)
        .then(() => TokenService.generate(username, password))
        .then((token) => {
          /* eslint-disable camelcase */
          const { data: { access_token } } = token;
          localStorageFacade.put(LocalStorageKeys.AUTH_TOKEN, `${access_token}`);
          TokenService.exchangeSso()
            .then(() => {
              window.location.href = `${env.api.track}#/telematic-accounts/create`;
            })
            .catch(() => {
              setSpin(false);
            });
        })
        .catch((e) => catchError(e));
    };

    const completeCustomerProspect = () => {
      CustomerProspectService.completeCustomerProspect(data)
        .then(() => {
          if (traceId) {
            GoogleAnalyticsService.client(traceId);
          }
        })
        .then(() => TokenService.generate(username, password))
        .then((token) => {
          /* eslint-disable camelcase */
          const { data: { access_token } } = token;
          localStorageFacade.put(LocalStorageKeys.AUTH_TOKEN, `${access_token}`);
          TokenService.exchangeSso()
            .then(() => {
              let redirectUrl;
              let queryParams = traceId === undefined ? undefined : `?traceId=${traceId}`;
              if (form.getFieldValue(Field.PROSPECT_TYPE) === ProspectType.SHIPPER) {
                redirectUrl = env.api.trackMonitoringsRequestsOutgoing;
              } else {
                redirectUrl = env.api.track;
                queryParams = (queryParams ? queryParams.concat('&') : '?').concat('successScreen=registration');
              }
              redirectUrl = queryParams ? `${redirectUrl}`.concat(queryParams) : redirectUrl;
              window.location.href = redirectUrl;
            })
            .catch(() => {
              setSpin(false);
            });
        })
        .catch((e) => catchError(e));
    };

    const updateProspect = () => {
      if (flowType === FlowType.ONBOARDING) {
        updateCompanyDataProspect();
      } else {
        completeCustomerProspect();
      }
    };

    const updateProspectWithVatNumberValidation = () => {
      const vatNumber = form.getFieldValue(Field.VAT_NUMBER);
      const taxCode = vatNumber.substring(0, 2);
      CompanyService.findCompany(taxCode, vatNumber.substring(2, vatNumber.Length))
        .then(() => {
          updateProspect();
        })
        .catch((error) => {
          if (error.response) {
            const companyExists = error.response.data.errorCode === ErrorCode.COMPANY_ALREADY_EXISTS;
            setExistCompany(companyExists);
            setErrorCode(error.response.data.errorCode);
            if (!companyExists) {
              updateProspect();
            } else {
              setSpin(false);
            }
          } else {
            setErrorCode(ErrorCode.COMPANY_EXTERNAL_VERIFICATION_FAILED);
            updateProspect();
          }
          setView(FormViewType.FILL_FORM);
        });
    };

    const updateProspectWithTaxNumberValidation = () => {
      switch (taxNumberType) {
        case TaxNumber.EIN:
          break;
        case TaxNumber.VAT:
        default:
          updateProspectWithVatNumberValidation();
          break;
      }
    };

    if (validatableTaxNumber) {
      updateProspectWithTaxNumberValidation();
    } else {
      updateProspect();
    }
  };

  const onChange = (v) => {
    const fieldToValid = getFieldToValid();
    if (v !== undefined && form.isFieldsTouched(fieldToValid, true)) {
      validateForm(fieldToValid);
    }
  };

  const warningExistCompany = () => {
    Modal.warning({
      title: t('FORM_FILL_COMPANY.MODAL_HEADER'),
      content: (
        <>
          <I18nextProvider i18n={translations.getTranslation(flowType)}>
            <Trans
              i18nKey="FORM_FILL_COMPANY.MODAL_CONTENT"
            >
              <a href={`mailto:${t('COMMON.EMAIL')}`}>{t('COMMON.EMAIL')}</a>
            </Trans>
          </I18nextProvider>
        </>
      ),
      onOk() { setExistCompany(false); },
      width: '482px',
    });
  };

  const warningErrorCreatedAccount = () => {
    Modal.warning({
      title: t('FORM_FILL_COMPANY.MODAL_ERROR'),
      content: (
        <>
          <I18nextProvider i18n={translations.getTranslation(flowType)}>
            <Trans
              i18nKey="FORM_FILL_COMPANY.MODAL_ERROR_CONTACT"
            >
              <a href="mailto:help@co3.io">help@co3.io</a>
            </Trans>
          </I18nextProvider>
        </>
      ),
      onOk() { setError(false); },
      width: '482px',
    });
  };

  useEffect(() => {
    if (existCompany) {
      warningExistCompany();
    }
  }, [existCompany]);

  useEffect(() => {
    if (isError) {
      warningErrorCreatedAccount();
    }
  }, [isError]);

  return (
    <>
      <CompanyDetailsCard style={{ height: '100%' }}>
        <Spin spinning={spin}>
          <Row>
            <Col span={24}>
              <FillCompanyHeader view={view} />
            </Col>
          </Row>
          <Form
            requiredMark={false}
            form={form}
            name="company-forms"
            onFinish={(v) => onFinish(v)}
            onChange={() => onChange(view)}
          >
            <Row
              style={{
                marginTop: '0px',
                marginLeft: '-3px',
                marginRight: '-10px',
              }}
            >
              {
                FormViewType.FILL_FORM === view ? (
                  <>
                    <Col span={1} />
                    <Col span={19}>
                      <VerificationInformation errorCode={errorCode} />
                    </Col>
                    <Col span={4} />
                  </>
                ) : (<></>)
              }
              <TaxNumberField taxNumberType={taxNumberType} findCompany={findCompany} form={form} view={view} setDisabledCountrySelect={setDisabledCountrySelect} />
            </Row>
            {
              view === undefined ? (
                <>
                  <Row style={{ height: '40px' }}>
                    <Col span={24} />
                  </Row>
                </>
              ) : (
                <>
                  <Row style={{
                    marginLeft: '-3px',
                    marginRight: '-3px',
                  }}
                  >
                    <Col span={1} />
                    <Col span={21}>
                      <FormCompanyData
                        form={form}
                        disabledCountrySelect={disabledCountrySelect}
                        regionFormConfig={regionFormConfig}
                        flowType={flowType}
                      />
                    </Col>
                    <Col span={2} />
                  </Row>
                  <Row style={{ height: '20px' }} />
                  <Row>
                    <Col span={1} />
                    <Col span={22}>
                      <InviteHr style={{ marginBottom: '30px' }} />
                    </Col>
                  </Row>
                  {
                    flowType === FlowType.REGISTRATION ? (
                      <Row style={{
                        marginLeft: '-3px',
                        marginRight: '-3px',
                      }}
                      >
                        <Col span={1} />
                        <Col span={21}>
                          <SelectProspectType FIELD={Field} />
                        </Col>
                        <Col span={2} />
                      </Row>
                    ) : (<></>)
                  }
                  <Form.Item style={{ marginBottom: '36px' }}>
                    <Row justify="center" style={{ height: '40px' }}>
                      <Col>
                        <ConfirmButton disabled={isSubmitIsActive} execute={() => form.submit()} icon={<RightOutlined />} />
                      </Col>
                    </Row>
                  </Form.Item>
                </>
              )
            }
          </Form>
        </Spin>
      </CompanyDetailsCard>
    </>
  );
};

export default FillCompanyData;

FillCompanyData.propTypes = {
  uuid: PropTypes.string.isRequired,
  regionFormConfig: PropTypes.any.isRequired,
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  flowType: PropTypes.oneOf(Object.values(FlowType)).isRequired,
  traceId: PropTypes.number,
};

FillCompanyData.defaultProps = {
  traceId: undefined,
};
